// ===========================================
// Admin Integrations Page
// Third-Party Integration Management
// ===========================================

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Tabs, Tab, Paper } from '@mui/material';
import {
  Dashboard as OverviewIcon,
  CalendarMonth as CalendarIcon,
  Folder as DriveIcon,
  Payment as StripeIcon,
  Sms as TwilioIcon,
  WhatsApp as WhatsAppIcon,
  AccountBalance as XeroIcon,
  Tag as SlackIcon,
} from '@mui/icons-material';
import { usePermissions } from '../../hooks/usePermissions';
import PageHeader from '../../components/common/PageHeader';
import IntegrationsOverviewTab from '../../components/admin/integrations/IntegrationsOverviewTab';
import GoogleCalendarTab from '../../components/admin/integrations/GoogleCalendarTab';
import GoogleDriveTab from '../../components/admin/integrations/GoogleDriveTab';
import StripeTab from '../../components/admin/integrations/StripeTab';
import TwilioTab from '../../components/admin/integrations/TwilioTab';
import WhatsAppTab from '../../components/admin/integrations/WhatsAppTab';
import XeroIntegrationTab from '../../components/admin/integrations/XeroIntegrationTab';
import SlackIntegrationTab from '../../components/admin/integrations/SlackIntegrationTab';

// ===========================================
// TYPES
// ===========================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// ===========================================
// TAB PANEL COMPONENT
// ===========================================

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`integrations-tabpanel-${index}`}
      aria-labelledby={`integrations-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `integrations-tab-${index}`,
    'aria-controls': `integrations-tabpanel-${index}`,
  };
}

// ===========================================
// MAIN COMPONENT
// ===========================================

type TabKey = 'overview' | 'calendar' | 'drive' | 'stripe' | 'twilio' | 'whatsapp' | 'xero' | 'slack';

interface TabDef {
  key: TabKey;
  label: string;
  icon: React.ReactElement;
  render: () => React.ReactNode;
}

const ALL_TABS: TabDef[] = [
  { key: 'overview', label: 'Overview', icon: <OverviewIcon />, render: () => <IntegrationsOverviewTab /> },
  { key: 'calendar', label: 'Google Calendar', icon: <CalendarIcon />, render: () => <GoogleCalendarTab /> },
  { key: 'drive', label: 'Google Drive', icon: <DriveIcon />, render: () => <GoogleDriveTab /> },
  { key: 'stripe', label: 'Stripe', icon: <StripeIcon />, render: () => <StripeTab /> },
  { key: 'twilio', label: 'Twilio (SMS)', icon: <TwilioIcon />, render: () => <TwilioTab /> },
  { key: 'whatsapp', label: 'WhatsApp', icon: <WhatsAppIcon />, render: () => <WhatsAppTab /> },
  { key: 'xero', label: 'Xero', icon: <XeroIcon />, render: () => <XeroIntegrationTab /> },
  { key: 'slack', label: 'Slack', icon: <SlackIcon />, render: () => <SlackIntegrationTab /> },
];

export default function AdminIntegrationsPage() {
  const [searchParams] = useSearchParams();
  const { can } = usePermissions();
  // Xero needs the invoice permission as well (roles plan 5.6); the tab index follows the VISIBLE list,
  // so `?tab=` resolves by key, never by a fixed position (an OAuth callback lands on `?tab=slack`, #384).
  const tabs = ALL_TABS.filter((t) => t.key !== 'xero' || can('operations_invoices', 'read'));
  const [activeKey, setActiveKey] = useState<TabKey>(() => {
    const wanted = searchParams.get('tab') ?? '';
    return tabs.find((t) => t.key === wanted)?.key ?? 'overview';
  });
  const activeTab = Math.max(0, tabs.findIndex((t) => t.key === activeKey));

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveKey(tabs[newValue].key);
  };

  return (
    <Box>
      <PageHeader
        title="Third-Party Integrations"
        subtitle="Connect and configure external services for calendar sync, accounting, messaging, and payments"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Integrations' },
        ]}
      />

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {tabs.map((t, i) => (
            <Tab key={t.key} icon={t.icon} iconPosition="start" label={t.label} {...a11yProps(i)} />
          ))}
        </Tabs>
      </Paper>

      {tabs.map((t, i) => (
        <TabPanel key={t.key} value={activeTab} index={i}>
          {t.render()}
        </TabPanel>
      ))}
    </Box>
  );
}
